/* ============================================================
   NOMA — product.js
   Variant picker with image swap, gallery, sticky bar
   ============================================================ */

(() => {
  'use strict';

  const $ = (s, ctx = document) => ctx.querySelector(s);
  const $$ = (s, ctx = document) => Array.from(ctx.querySelectorAll(s));

  /* ============================================================
     Product gallery
     ============================================================ */
  class ProductGallery {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector('.product-gallery__media');
      this.slides = $$('.product-gallery__slide', root);
      this.thumbs = $$('.product-gallery__thumb', root);
      this.dots = $$('.product-gallery__dot', root);
      this.counter = root.querySelector('.product-gallery__counter');
      this.index = 0;
      this.bind();
      this.syncFromScroll();
    }
    bind() {
      this.thumbs.forEach((t, i) => t.addEventListener('click', () => this.go(i)));
      this.dots.forEach((d, i) => d.addEventListener('click', () => this.go(i)));
      if (this.track) {
        let scrollTimeout;
        this.track.addEventListener('scroll', () => {
          clearTimeout(scrollTimeout);
          scrollTimeout = setTimeout(() => this.syncFromScroll(), 60);
        }, { passive: true });
      }
    }
    go(i) {
      if (!this.slides[i] || !this.track) return;
      this.index = i;
      const slide = this.slides[i];
      this.track.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
      this.updateUI();
    }
    syncFromScroll() {
      if (!this.track) return;
      const slideWidth = this.track.clientWidth;
      const i = Math.round(this.track.scrollLeft / slideWidth);
      if (i !== this.index) {
        this.index = i;
        this.updateUI();
      }
    }
    updateUI() {
      this.thumbs.forEach((t, i) => t.classList.toggle('is-active', i === this.index));
      this.dots.forEach((d, i) => d.classList.toggle('is-active', i === this.index));
      if (this.counter) {
        this.counter.textContent = `${this.index + 1} / ${this.slides.length}`;
      }
    }
    showVariantImage(mediaId) {
      if (!mediaId) return;
      const idx = this.slides.findIndex(s => s.dataset.mediaId === String(mediaId));
      if (idx >= 0) this.go(idx);
    }
  }

  /* ============================================================
     Variant picker — full variant matrix logic
     ============================================================ */
  class VariantPicker {
    constructor(root) {
      this.root = root;
      try {
        this.data = JSON.parse(root.querySelector('[data-product-json]').textContent);
      } catch (e) { console.warn('NOMA: product json missing'); return; }
      this.form = $('form[data-product-form]', root) || root.closest('[data-product-form]') || $('form[data-product-form]');
      this.inputs = $$('input[data-variant-input]', root);
      this.priceEl = $('[data-price]', root);
      this.priceCompareEl = $('[data-price-compare]', root);
      this.addBtn = $('[data-add-to-cart]', root);
      this.addBtnLabel = this.addBtn ? this.addBtn.querySelector('[data-add-to-cart-label]') : null;
      this.variantIdInput = $('input[name="id"]', this.form);
      this.gallery = window.productGallery;
      this.optionValueEls = $$('[data-option-value]', root);
      this.update();
      this.bind();
    }
    bind() {
      this.inputs.forEach(i => i.addEventListener('change', () => this.update()));
    }
    selectedOptions() {
      const opts = [];
      const groups = {};
      this.inputs.forEach(input => {
        if (input.checked) {
          groups[input.dataset.optionPosition] = input.value;
        }
      });
      const sortedKeys = Object.keys(groups).sort((a, b) => parseInt(a) - parseInt(b));
      return sortedKeys.map(k => groups[k]);
    }
    findVariant(selected) {
      return this.data.variants.find(v => {
        return v.options.every((opt, i) => opt === selected[i]);
      });
    }
    update() {
      const selected = this.selectedOptions();
      const variant = this.findVariant(selected);

      // labels
      this.optionValueEls.forEach(el => {
        const pos = parseInt(el.dataset.optionValue, 10);
        if (selected[pos - 1]) el.textContent = selected[pos - 1];
      });

      if (!variant) {
        if (this.addBtn) {
          this.addBtn.disabled = true;
          if (this.addBtnLabel) this.addBtnLabel.textContent = window.theme.strings.unavailable || 'Unavailable';
        }
        return;
      }

      // hidden id
      if (this.variantIdInput) this.variantIdInput.value = variant.id;

      // price
      if (this.priceEl) this.priceEl.textContent = window.formatMoney(variant.price);
      if (this.priceCompareEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          this.priceCompareEl.textContent = window.formatMoney(variant.compare_at_price);
          this.priceCompareEl.hidden = false;
        } else {
          this.priceCompareEl.hidden = true;
        }
      }

      // availability
      if (this.addBtn) {
        if (variant.available) {
          this.addBtn.disabled = false;
          if (this.addBtnLabel) this.addBtnLabel.textContent = window.theme.strings.addToCart || 'Add to cart';
        } else {
          this.addBtn.disabled = true;
          if (this.addBtnLabel) this.addBtnLabel.textContent = window.theme.strings.soldOut || 'Sold out';
        }
      }

      // url
      if (variant.id && history.replaceState) {
        const url = new URL(window.location);
        url.searchParams.set('variant', variant.id);
        history.replaceState({}, '', url);
      }

      // gallery image swap
      if (this.gallery && variant.featured_media) {
        this.gallery.showVariantImage(variant.featured_media.id);
      }

      // unavailable swatches feedback
      this.flagUnavailableOptions(selected);
    }
    flagUnavailableOptions(selected) {
      const grouped = {};
      this.inputs.forEach(input => {
        const pos = parseInt(input.dataset.optionPosition, 10);
        if (!grouped[pos]) grouped[pos] = [];
        grouped[pos].push(input);
      });
      Object.keys(grouped).forEach(posKey => {
        const pos = parseInt(posKey, 10);
        grouped[pos].forEach(input => {
          const trial = [...selected];
          trial[pos - 1] = input.value;
          const v = this.findVariant(trial);
          const label = input.closest('[data-option-label]');
          if (!label) return;
          if (!v || !v.available) label.setAttribute('data-unavailable', '');
          else label.removeAttribute('data-unavailable');
        });
      });
    }
  }

  /* ============================================================
     Product form — AJAX add to cart
     ============================================================ */
  class ProductForm {
    constructor(form) {
      this.form = form;
      this.btn = form.querySelector('[data-add-to-cart]');
      this.btnLabel = form.querySelector('[data-add-to-cart-label]');
      this.bind();
    }
    bind() {
      this.form.addEventListener('submit', async (e) => {
        e.preventDefault();
        if (this.btn.disabled) return;
        const originalLabel = this.btnLabel ? this.btnLabel.textContent : '';
        this.btn.disabled = true;
        if (this.btnLabel) this.btnLabel.textContent = window.theme.strings.loading || 'Loading…';

        const formData = new FormData(this.form);
        const payload = {};
        for (const [k, v] of formData.entries()) payload[k] = v;

        if (window.cartDrawer) {
          const ok = await window.cartDrawer.addItem(payload);
          if (this.btnLabel) {
            this.btnLabel.textContent = ok ? '✓ Ajouté' : 'Erreur';
            setTimeout(() => { if (this.btnLabel) this.btnLabel.textContent = originalLabel; }, 1600);
          }
        }
        this.btn.disabled = false;
      });
    }
  }

  /* ============================================================
     Qty stepper (PDP)
     ============================================================ */
  const initQty = () => {
    $$('.qty').forEach(group => {
      const input = group.querySelector('input');
      if (!input) return;
      group.addEventListener('click', (e) => {
        const plus = e.target.closest('[data-qty="plus"]');
        const minus = e.target.closest('[data-qty="minus"]');
        if (!plus && !minus) return;
        const cur = parseInt(input.value, 10) || 1;
        const next = plus ? cur + 1 : Math.max(1, cur - 1);
        input.value = next;
        input.dispatchEvent(new Event('change'));
      });
    });
  };

  /* ============================================================
     Sticky add-to-cart bar (mobile)
     ============================================================ */
  const initStickyBar = () => {
    const bar = $('.product-sticky-bar');
    const trigger = $('[data-sticky-trigger]') || $('.product-form__add');
    if (!bar || !trigger) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => bar.classList.toggle('is-visible', !e.isIntersecting));
    }, { threshold: 0 });
    io.observe(trigger);

    // Sticky button forwards click to main add-to-cart button
    const stickyBtn = bar.querySelector('[data-sticky-add]');
    if (stickyBtn) {
      stickyBtn.addEventListener('click', (e) => {
        e.preventDefault();
        const mainBtn = document.querySelector('[data-add-to-cart]');
        if (mainBtn && !mainBtn.disabled) mainBtn.click();
      });
    }
  };

  /* ============================================================
     Init
     ============================================================ */
  const init = () => {
    const gallery = $('.product-gallery');
    if (gallery) window.productGallery = new ProductGallery(gallery);

    const picker = $('[data-variant-picker]');
    if (picker) new VariantPicker(picker);

    $$('form[data-product-form]').forEach(f => new ProductForm(f));

    initQty();
    initStickyBar();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
